import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Table, Pagination, Icon, Dropdown, Label } from 'semantic-ui-react';

import DeviceModalContainer from '../../containers/DeviceModalContainer';

import styles from './Devices.module.scss';

const Devices = React.memo(
  ({
    realtimeDatas,
    updatedAt,
    channels,
    models,
    devices,
    items,
    isDeviceModalOpened,
    onFetchRealtimeDatas,
  }) => {
    const [t] = useTranslation();

    const [currentPage, setPage] = useState(1);
    const [data, setData] = useState([]);
    const [intervalId, setIntervalId] = useState();
    const [intervalData, setIntervalData] = useState('continous');
    const [listModels, setListModels] = useState([]);
    const [filterModel, setfilterModel] = useState(listModels[0]?.value || 0);
    const [listItems, setListItems] = useState([]);

    const resultsPerPage = 100;
    const totalPages = Math.ceil(devices.length / resultsPerPage);

    const dateShowFormat = 'dd/MM/yyyy HH:mm:ss';

    const onPageChange = (e, { activePage }) => {
      setPage(activePage);
    };

    const getfilterModel = (event, { value }) => {
      setfilterModel(value);

      setListItems([]);
      items.forEach((item) => {
        if (item.modelId === value) {
          setListItems((oldArray) => [...oldArray, item]);
        }
      });
    };

    useEffect(() => {
      const newListModels = [];

      models?.forEach((model, index) => {
        newListModels.push({
          key: index,
          value: model.id,
          text: model.name,
        });
      });

      setListModels(newListModels);
      setfilterModel(newListModels[0]?.value || 0);
      setListItems([]);
      items.forEach((item) => {
        if (item.modelId === newListModels[0]?.value || 0) {
          setListItems((oldArray) => [...oldArray, item]);
        }
      });
    }, [items, models]);

    useEffect(() => {
      setData(devices.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage));
    }, [devices, currentPage]);

    const intervalOptions = [
      { key: '1', value: 'continous', text: 'continous' },
      { key: '5', value: '5', text: '05S' },
      { key: '10', value: '10', text: '10S' },
      { key: '15', value: '15', text: '15S' },
      { key: '30', value: '30', text: '30S' },
      { key: '60', value: '60', text: '01M' },
      { key: '120', value: '120', text: '02M' },
      { key: '300', value: '300', text: '05M' },
      { key: '600', value: '600', text: '10M' },
      { key: '900', value: '900', text: '15M' },
      { key: '1800', value: '1800', text: '30M' },
    ];

    const handleChangeInterval = useCallback(
      (e, dataValue) => {
        setIntervalData(dataValue.value);
        if (dataValue.value && dataValue.value !== 'continous') {
          clearInterval(intervalId);
          const intervalNew = setInterval(() => {
            onFetchRealtimeDatas();
          }, dataValue.value * 1000);
          setIntervalId(intervalNew);
        } else {
          clearInterval(intervalId);
        }
      },
      [intervalId, onFetchRealtimeDatas],
    );

    return (
      <div className={styles.devicesWrapper}>
        <h2 className={styles.pageTitle}>{t('common.realtimeMeasureValueTable')}</h2>
        <div className={styles.tableHeader}>
          <div className={styles.updatePeriod}>
            <Label>{t('common.devices.model')}: </Label>
            <Dropdown
              placeholder={t('common.devices.model')}
              selection
              value={filterModel || listModels[0]?.value || 0}
              options={listModels}
              onChange={getfilterModel}
            />
            {false && (
              <>
                <Label>{t('common.updatePeriod')}: </Label>
                <Dropdown
                  placeholder={t('common.updatePeriod')}
                  selection
                  value={intervalData}
                  options={intervalOptions}
                  onChange={handleChangeInterval}
                />
              </>
            )}
          </div>
          <div className={styles.deviceUpdatedAt}>
            {`${t('common.updatedAt')}: ${format(new Date(updatedAt), dateShowFormat)}`}
          </div>
        </div>
        {/* style={{ display: 'block', 'overflow-x': 'scroll' }} */}
        <div className={styles.tableWrapper}>
          <Table singleLine>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell className="text-xs text-grey">
                  {t('common.devices.name')}
                </Table.HeaderCell>
                {listItems.map((item) => (
                  <Table.HeaderCell key={item.id} className="text-xs text-grey" textAlign="center">
                    {/* {`${item.name} (${item.unitName})`} */}
                    {item.name}
                    <br />
                    {item.unitName}
                  </Table.HeaderCell>
                ))}
                <Table.HeaderCell className="text-xs text-grey">
                  {t('common.brands.update')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.map((device) => {
                let deviceItems;
                let isError = false;
                let modelItem = device.modelId;

                if (device.modelId === filterModel) {
                  if (intervalData === 'continous') {
                    deviceItems = device.items;
                    isError = device.isError;
                  } else {
                    const realtimeData = realtimeDatas?.find((rt) => rt.deviceId === device.id);
                    deviceItems = realtimeData?.values;
                    isError = realtimeData?.isError;
                  }
                  let channelItem = device.channelId;
                  if (!channelItem.name) {
                    channelItem = channels.find((channel) => channel.id === device.channelId);
                  }

                  if (!modelItem.name) {
                    modelItem = models.find((model) => model.id === device.modelId);
                  }

                  return (
                    <Table.Row key={device.id}>
                      <Table.Cell className={`${styles.deviceName} ${isError && styles.isError}`}>
                        <span>{device.name}</span>
                      </Table.Cell>
                      {listItems.map((item) => (
                        <Table.Cell key={item.id} textAlign="center">
                          <span className={styles.brand_item}>
                            {!isError && !Number.isNaN(deviceItems?.[item.itemName] * 1)
                              ? (deviceItems?.[item.itemName] * 1).toLocaleString('en-GB', {
                                  minimumFractionDigits: item.decimalPoint,
                                  maximumFractionDigits: item.decimalPoint,
                                })
                              : '-'}
                          </span>
                        </Table.Cell>
                      ))}
                      <Table.Cell className={styles.brand_item} textAlign="center">
                        {!isError && device.loggedAt
                          ? t('format:time', {
                              postProcess: 'formatDate',
                              value: new Date(device.loggedAt),
                            })
                          : '-'}
                      </Table.Cell>
                    </Table.Row>
                  );
                }

                return <Table.Row key={device.id} />;
              })}
            </Table.Body>
          </Table>
        </div>
        <div className={`text-right ${styles.paginationWrapper}`}>
          <Pagination
            activePage={currentPage}
            boundaryRange={0}
            onPageChange={onPageChange}
            size="mini"
            siblingRange={1}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
            firstItem={{ content: <Icon name="angle double left" />, icon: true }}
            lastItem={{ content: <Icon name="angle double right" />, icon: true }}
            totalPages={totalPages}
          />
        </div>
        {isDeviceModalOpened && <DeviceModalContainer />}
      </div>
    );
  },
);

Devices.propTypes = {
  realtimeDatas: PropTypes.arrayOf(PropTypes.any),
  updatedAt: PropTypes.instanceOf(Date),
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  devices: PropTypes.arrayOf(PropTypes.any).isRequired,
  channels: PropTypes.arrayOf(PropTypes.any).isRequired,
  models: PropTypes.arrayOf(PropTypes.any).isRequired,
  isDeviceModalOpened: PropTypes.bool.isRequired,
  onFetchRealtimeDatas: PropTypes.func.isRequired,
};

Devices.defaultProps = {
  realtimeDatas: undefined,
  updatedAt: undefined,
};

export default Devices;
